// AIEditableWrapper - Wraps content with an AI edit button for admins

import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { ContentVersionPopover } from './ContentVersionPopover';
import type { ContentComponentType } from '../../../hooks/unified/useContentVersionsQueries';

interface AIEditableWrapperProps {
  jobId: string;
  componentType: ContentComponentType;
  children: React.ReactNode;
  onContentUpdated?: (newContent: string | string[]) => void;
  className?: string;
}

export function AIEditableWrapper({
  jobId,
  componentType,
  children,
  onContentUpdated,
  className = '',
}: AIEditableWrapperProps) {
  const { isAdmin } = useAuth();
  const [isPopoverOpen, setPopoverOpen] = useState(false);

  // Don't show AI button for non-admins
  if (!isAdmin) {
    return <>{children}</>;
  }

  return (
    <div className={`relative group ${className}`}>
      {children}

      {/* AI Edit Button - 30% opacity, full on hover */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPopoverOpen(true);
        }}
        className="absolute top-2 right-2 opacity-30 hover:opacity-100 
                   transition-all duration-200 bg-purple-600 hover:bg-purple-700 
                   rounded-full p-2 shadow-lg z-20 hover:scale-110"
        title={`Edit ${componentType} with AI`}
      >
        <svg
          className="w-4 h-4 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      </button>

      {/* Content Version Popover */}
      <ContentVersionPopover
        isOpen={isPopoverOpen}
        onClose={() => setPopoverOpen(false)}
        jobId={jobId}
        componentType={componentType}
        onContentUpdated={onContentUpdated}
      />
    </div>
  );
}
